// filepath: apps/web/src/features/proc/RetryStepButton.tsx
//
// FR-UI-PROC-1 AC-5 — retry-step action button for a failed
// instance row.
//
// The button invokes `useRetryStep` on click. It is disabled while
// the mutation is in flight (with explanatory chrome). On 409
// `not_failed` the row component re-renders the FR-UI-CORE-10
// stale-version state — this component renders a typed error
// pill so the row can show the inline error reason.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useRetryStep } from "./useRetryStep";
import type { FailedInstance } from "./types";

export interface RetryStepButtonProps {
  instance: FailedInstance;
  /**
   * Called when the mutation succeeds. The parent (row) uses this
   * to invalidate TanStack Query and refresh the list view.
   */
  onSuccess?: () => void;
  /**
   * Called when the server returns 409 `not_failed` (the row's
   * `state` is no longer `failed`). The parent renders the
   * FR-UI-CORE-10 stale-version state.
   */
  onConflict?: () => void;
}

export function RetryStepButton({
  instance,
  onSuccess,
  onConflict,
}: RetryStepButtonProps): React.ReactElement {
  const { t } = useTranslation();
  const { mutate, isPending, error } = useRetryStep({
    onSuccess: () => onSuccess?.(),
    onConflict: () => onConflict?.(),
  });

  const label = isPending
    ? t("failed_instance.retry_pending", "Retrying…")
    : t("failed_instance.retry_button", "Retry step");

  return (
    <button
      type="button"
      data-testid="retry-step-button"
      data-instance-id={instance.id}
      disabled={isPending}
      aria-busy={isPending}
      onClick={() => {
        mutate({ instanceId: instance.id });
      }}
      className="retry-step-button"
    >
      {label}
      {error !== null && (
        <span
          data-testid="retry-step-button-error"
          className="retry-step-button-error"
          role="alert"
        >
          {error.code === "not_failed"
            ? t(
                "failed_instance.retry_not_failed",
                "Instance is no longer in failed state. Refresh to see the latest.",
              )
            : t("failed_instance.retry_error", "Retry failed.")}
        </span>
      )}
    </button>
  );
}
